import { Link } from "react-router-dom";
import {
  Compass,
  HeartHandshake,
  Sparkles,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
} from "lucide-react";
import { SITE } from "@/constants/site";
import { Seo } from "@/components/site/Seo";

const heroImage =
  "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=1400&q=80";

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Brisbane's Expert NDIS Support Coordination"
        description="Brisbane's Expert NDIS Support Coordination — Kernected Disability connects participants and families across Queensland and Australia with the right services, so your plan delivers real outcomes."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Kernected Disability Support Services",
          url: "https://kernected.org",
          email: "KERNECTEDDISABILITYSUPPORT@proton.me",
          areaServed: "AU",
          serviceType: "NDIS Support Coordination",
          image:
            "https://customer-assets.emergentagent.com/job_ndis-support-hub-4/artifacts/3r31q55x_kernected%20logo%202.png",
          description:
            "Brisbane based NDIS Support Coordination provider serving participants and families across Queensland and Australia.",
        }}
      />
      {/* HERO */}
      <section
        className="relative overflow-hidden"
        data-testid="home-hero"
      >
        <div className="container-narrow grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 md:py-24">
          <div className="fade-up">
            <span
              data-testid="hero-brisbane-badge"
              className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-full"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#1B3A6B]" aria-hidden="true" />
              Brisbane-based · Supporting Queensland and Australia
            </span>
            <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-[3.75rem] font-medium tracking-tight text-slate-900 leading-[1.05]">
              Brisbane's Expert NDIS Support Coordination
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed max-w-xl">
              We connect participants and families across Queensland and
              Australia with the right services, so your NDIS plan delivers
              real support, real choices and real outcomes.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-enquire"
                className="inline-flex items-center justify-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] px-7 py-3.5 rounded-lg font-medium transition-colors"
              >
                Get support today <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B3A6B] border-2 border-[#1B3A6B] hover:bg-[#F0F4F8] px-7 py-3.5 rounded-lg font-medium transition-colors"
              >
                Explore our services
              </Link>
            </div>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl">
              {[
                { icon: ShieldCheck, label: "NDIS-aligned" },
                { icon: HeartHandshake, label: "Person centred" },
                { icon: Sparkles, label: "Plain language" },
              ].map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-center gap-2 text-sm text-slate-700"
                >
                  <Icon className="h-5 w-5 text-[#2A6496]" aria-hidden="true" />
                  {label}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <img
              src={heroImage}
              alt="A warm, candid moment between a participant and their support coordinator"
              loading="eager"
              className="w-full h-[420px] md:h-[560px] object-cover rounded-3xl"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* WHAT IS SUPPORT COORDINATION */}
      <section className="bg-white border-y border-slate-200" data-testid="home-explainer">
        <div className="container-narrow py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-sm font-semibold tracking-[0.18em] uppercase text-[#1B3A6B]">
              What is Support Coordination?
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-snug">
              Your NDIS plan, made simple to use
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              Support Coordination is a funded NDIS service that helps you
              understand your plan, connect with the right providers, and build
              the skills to manage supports with confidence.
            </p>
            <Link
              to="/services"
              data-testid="explainer-cta-services"
              className="mt-7 inline-flex items-center gap-2 font-semibold text-[#1B3A6B] hover:text-[#132A4D]"
            >
              See how we help <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: ClipboardList,
                title: "Understand your plan",
                copy: "We translate the NDIS into plain language so you know exactly what is funded and why.",
              },
              {
                icon: Compass,
                title: "Find the right providers",
                copy: "We help you choose providers that match your goals, culture, and communication needs.",
              },
              {
                icon: HeartHandshake,
                title: "Coordinate your supports",
                copy: "From service agreements to schedules, we keep your supports running smoothly.",
              },
              {
                icon: ShieldCheck,
                title: "Build your confidence",
                copy: "Over time, we help you build the skills and knowledge to direct your own supports.",
              },
            ].map(({ icon: Icon, title, copy }) => (
              <li
                key={title}
                className="bg-[#FDFBF7] rounded-2xl border border-slate-200 p-7"
              >
                <Icon className="h-6 w-6 text-[#2A6496]" aria-hidden="true" />
                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-[15px] text-slate-700 leading-relaxed">
                  {copy}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="container-narrow py-16 md:py-24" data-testid="home-services">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold tracking-[0.18em] uppercase text-[#1B3A6B]">
            Our services
          </p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-snug">
            Two ways we walk beside you
          </h2>
          <p className="mt-4 text-lg text-slate-700">
            Whether your plan is straightforward or complex, our coordinators
            tailor support around you.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {[
            {
              tag: "Level 2",
              title: "Support Coordination",
              copy: "Connect your plan to providers, supports and community resources that match your goals.",
            },
            {
              tag: "Always included",
              title: "Plan Implementation",
              copy: "We turn your NDIS plan into clear steps, service agreements and a working schedule.",
            },
          ].map((s) => (
            <article
              key={s.title}
              data-testid={`home-service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-white rounded-2xl border border-slate-200/70 p-8 hover:border-[#1B3A6B]/30 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#2A6496] bg-[#F0F4F8] px-2.5 py-1 rounded-full">
                {s.tag}
              </span>
              <h3 className="mt-5 font-display text-2xl font-semibold text-slate-900">
                {s.title}
              </h3>
              <p className="mt-3 text-[15px] text-slate-700 leading-relaxed">
                {s.copy}
              </p>
              <Link
                to="/services"
                className="mt-6 inline-flex items-center gap-2 font-semibold text-[#1B3A6B] group-hover:gap-3 transition-all"
              >
                Learn more <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* WHY KERNECTED */}
      <section className="bg-[#1B3A6B] text-white" data-testid="home-why">
        <div className="container-narrow py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-sm font-semibold tracking-[0.18em] uppercase text-[#7AB4DF]">
              Why families choose us
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-snug">
              Capable, and persistent on your behalf
            </h2>
            <p className="mt-5 text-lg text-slate-100/90 leading-relaxed">
              We believe that the right support starts with being heard. We take
              time to understand your goals, your routine and your preferences,
              then we build supports that actually fit.
            </p>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              "No jargon, no judgement",
              "Responsive and easy to reach",
              "Independent. We work for you",
              "Culturally safe and inclusive",
              "Strong provider network",
              "Ready when crisis hits",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-5"
              >
                <CheckCircle2 className="h-5 w-5 text-[#7AB4DF] flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-[15px]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-narrow py-16 md:py-24" data-testid="home-cta">
        <div className="bg-white border border-slate-200/70 rounded-3xl p-10 md:p-14 grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-snug">
              Ready to Unlock the Full Value of your NDIS Plan?
            </h2>
            <p className="mt-4 text-lg text-slate-700">
              Tell us a little about your situation. We'll respond within one
              business day with clear next steps.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
            <Link
              to="/contact"
              data-testid="bottom-cta-enquire"
              className="inline-flex items-center justify-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] px-6 py-3.5 rounded-lg font-medium"
            >
              Start an enquiry
            </Link>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="bottom-cta-email"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1B3A6B] border-2 border-[#1B3A6B] hover:bg-[#F0F4F8] px-6 py-3.5 rounded-lg font-medium"
            >
              Email us directly
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
